import { z } from 'zod';
import { CountryCodeSchema, type CountryCode } from './geo';

/* ------------------------------------------------------------------ */
/* Go-live readiness (W23)                                             */
/*                                                                     */
/* Per-town feature switches that can only narrow the global FEATURE_* */
/* flags, pilot gates per town (hourly snapshots), app versions with a */
/* "please update" sheet, launch blockers and the launch-day checklist. */
/* ------------------------------------------------------------------ */

/* ---------------- Town feature switches ---------------- */

export const TOWN_FEATURE_KEYS = [
  'ordering',
  'cod',
  'online_payments',
  'wallet',
  'dispatch_gig',
  'dispatch_own_riders',
  'tracking_live',
  'support_chat',
  'reviews',
  'notifications_marketing',
  'growth_referrals',
  'campus_pass',
  'cashback',
  'public_site_listing',
  'mi_field',
  'partner_insights',
] as const;
export const TownFeatureKeySchema = z.enum(TOWN_FEATURE_KEYS);
export type TownFeatureKey = z.infer<typeof TownFeatureKeySchema>;

/** off → nobody; staff_only → staff test accounts; pilot → allow-list + staff; live → everyone. */
export const TOWN_FEATURE_STATES = ['off', 'staff_only', 'pilot', 'live'] as const;
export const TownFeatureStateSchema = z.enum(TOWN_FEATURE_STATES);
export type TownFeatureState = z.infer<typeof TownFeatureStateSchema>;

/** Order of openness (used to tell "promote" from "pull back"). */
export const TOWN_FEATURE_STATE_RANK: Record<TownFeatureState, number> = {
  off: 0,
  staff_only: 1,
  pilot: 2,
  live: 3,
};

export const TOWN_FEATURE_STATE_LABELS: Record<TownFeatureState, string> = {
  off: 'Off',
  staff_only: 'Staff only',
  pilot: 'Pilot',
  live: 'Live',
};

export interface TownFeatureDef {
  key: TownFeatureKey;
  label: string;
  description: string;
  /** The global env master switch(es). All must be on for the town switch to matter. */
  envFlags: readonly string[];
  /** Where the switch is enforced (shown in Capsico OS and the runbook). */
  enforcedAt: string;
  /** Whether a "pilot" allow-list of restaurants means anything for this feature. */
  usesRestaurantAllowList: boolean;
}

/** The registry (mirrored by backend/src/config/town-features.ts). */
export const TOWN_FEATURE_DEFS: readonly TownFeatureDef[] = [
  {
    key: 'ordering',
    label: 'Ordering',
    description:
      'Customers can see restaurants and place orders. Off = "coming soon" with the area vote.',
    envFlags: ['FEATURE_DISCOVERY', 'FEATURE_CHECKOUT'],
    enforcedAt: 'Home feed, search, restaurant page, quote and place order',
    usesRestaurantAllowList: true,
  },
  {
    key: 'cod',
    label: 'Cash on delivery',
    description: 'Cash on delivery is offered at checkout.',
    envFlags: ['FEATURE_CHECKOUT'],
    enforcedAt: 'Checkout payment options',
    usesRestaurantAllowList: false,
  },
  {
    key: 'online_payments',
    label: 'Online payments',
    description: 'UPI, cards, net banking and wallets through the payment gateways.',
    envFlags: ['FEATURE_PAYMENTS'],
    enforcedAt: 'Checkout payment options, place order, pay again',
    usesRestaurantAllowList: false,
  },
  {
    key: 'wallet',
    label: 'Capsico Money',
    description: 'Capsico Money can be spent at checkout.',
    envFlags: ['FEATURE_PAYMENTS'],
    enforcedAt: 'Checkout wallet balance',
    usesRestaurantAllowList: false,
  },
  {
    key: 'dispatch_gig',
    label: 'Gig riders',
    description: 'Capsico gig riders receive trip offers in this town.',
    envFlags: ['FEATURE_DISPATCH'],
    enforcedAt: 'Dispatch candidate pool',
    usesRestaurantAllowList: false,
  },
  {
    key: 'dispatch_own_riders',
    label: 'Restaurant own riders',
    description: "Restaurants' own riders receive their outlets' trips.",
    envFlags: ['FEATURE_DISPATCH'],
    enforcedAt: 'Dispatch candidate pool',
    usesRestaurantAllowList: true,
  },
  {
    key: 'tracking_live',
    label: 'Live tracking',
    description: 'Live rider map on the order screen (registry only; tracking itself stays on).',
    envFlags: ['FEATURE_TRACKING'],
    enforcedAt: 'Registry only (W14 tracking is not narrowed in W23)',
    usesRestaurantAllowList: false,
  },
  {
    key: 'support_chat',
    label: 'Help chat',
    description: 'In-app help chat (registry only).',
    envFlags: ['FEATURE_SUPPORT'],
    enforcedAt: 'Registry only (support stays reachable everywhere)',
    usesRestaurantAllowList: false,
  },
  {
    key: 'reviews',
    label: 'Ratings & reviews',
    description: 'Customers rate orders (registry only).',
    envFlags: ['FEATURE_TRUST'],
    enforcedAt: 'Registry only',
    usesRestaurantAllowList: false,
  },
  {
    key: 'notifications_marketing',
    label: 'Marketing messages',
    description: 'Cart reminders, first-order nudges and win-back messages.',
    envFlags: ['FEATURE_NOTIFY'],
    enforcedAt: 'Notification journeys (cart, first order, win-back)',
    usesRestaurantAllowList: false,
  },
  {
    key: 'growth_referrals',
    label: 'Refer & earn',
    description: 'Referral codes and the referral reward.',
    envFlags: ['FEATURE_GROWTH'],
    enforcedAt: 'Refer & earn screens, codes, reward on first delivery',
    usesRestaurantAllowList: false,
  },
  {
    key: 'campus_pass',
    label: 'Campus Pass',
    description: 'Student Campus Pass (free delivery to campus drop points).',
    envFlags: ['FEATURE_GROWTH'],
    enforcedAt: 'Campus Pass screens',
    usesRestaurantAllowList: false,
  },
  {
    key: 'cashback',
    label: 'Cashback',
    description: 'Capsico Money cashback on delivered orders.',
    envFlags: ['FEATURE_GROWTH'],
    enforcedAt: 'Cashback screens and the credit on delivery',
    usesRestaurantAllowList: false,
  },
  {
    key: 'public_site_listing',
    label: 'Public website',
    description: 'The town and its restaurants appear on the public website (live only).',
    envFlags: ['FEATURE_PUBLIC_SITE'],
    enforcedAt: 'Public site API (towns, restaurants, sitemap)',
    usesRestaurantAllowList: false,
  },
  {
    key: 'mi_field',
    label: 'Market Initialers',
    description: 'MI field work (beats, prospects, visits) in this town.',
    envFlags: ['FEATURE_MI'],
    enforcedAt: 'MI app town list',
    usesRestaurantAllowList: false,
  },
  {
    key: 'partner_insights',
    label: 'Partner insights',
    description: 'Hub insights in the Partner app (registry only).',
    envFlags: ['FEATURE_INSIGHTS'],
    enforcedAt: 'Registry only',
    usesRestaurantAllowList: false,
  },
];

export const TOWN_FEATURE_LABELS: Record<TownFeatureKey, string> = Object.fromEntries(
  TOWN_FEATURE_DEFS.map((def) => [def.key, def.label]),
) as Record<TownFeatureKey, string>;

/** Pilot allow-lists are capped so the cache stays small. */
export const PILOT_PHONES_MAX = 500;
export const PILOT_RESTAURANTS_MAX = 200;

export const TownFeatureCellSchema = z.object({
  key: TownFeatureKeySchema,
  /** The stored state (or the default when no row exists). */
  state: TownFeatureStateSchema,
  /** "row" when Capsico OS set it, "default" when LAUNCH_DEFAULT_TOWN_STATE applies. */
  source: z.enum(['row', 'default']),
  /** The env master switch(es) are all on. */
  envOn: z.boolean(),
  /** What customers actually get: "off" whenever the env switch is off. */
  effective: TownFeatureStateSchema,
  pilotPhoneCount: z.number().int().min(0),
  pilotRestaurantCount: z.number().int().min(0),
  changedBy: z.string().nullable(),
  changedByName: z.string().nullable(),
  reason: z.string().nullable(),
  changedAt: z.string().nullable(),
  /** null when no row exists yet (first change sends expectedVersion null). */
  version: z.number().int().min(1).nullable(),
});
export type TownFeatureCell = z.infer<typeof TownFeatureCellSchema>;

export const TownFeatureRowSchema = z.object({
  townId: z.uuid(),
  townName: z.string(),
  country: CountryCodeSchema,
  townStatus: z.enum(['planned', 'live', 'paused']),
  cells: z.array(TownFeatureCellSchema),
});
export type TownFeatureRow = z.infer<typeof TownFeatureRowSchema>;

export const TownFeatureMatrixSchema = z.object({
  defaultState: z.enum(['live', 'off']),
  /** You may change switches (launch.manage, not a Captain-only view). */
  canManage: z.boolean(),
  /** Only an owner may promote past red gates. */
  canOverride: z.boolean(),
  towns: z.array(TownFeatureRowSchema),
});
export type TownFeatureMatrix = z.infer<typeof TownFeatureMatrixSchema>;

export const TownFeatureMatrixQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
});
export type TownFeatureMatrixQuery = z.infer<typeof TownFeatureMatrixQuerySchema>;

const PilotPhoneSchema = z
  .string()
  .trim()
  .regex(/^\+(91[6-9][0-9]{9}|9779[678][0-9]{8})$/, 'Enter phone numbers with the country code');

export const SetTownFeatureInputSchema = z.object({
  state: TownFeatureStateSchema,
  reason: z.string().trim().min(5, 'Say why (at least 5 characters)').max(300),
  /** null when the cell has no stored row yet. */
  expectedVersion: z.number().int().min(1).nullable(),
  /** Replaces the allow-list when given (pilot only; kept for later otherwise). */
  pilotPhones: z.array(PilotPhoneSchema).max(PILOT_PHONES_MAX).optional(),
  pilotRestaurantIds: z.array(z.uuid()).max(PILOT_RESTAURANTS_MAX).optional(),
  /** Must be true for any change to "live" (the confirm dialog). */
  confirmLive: z.boolean().optional(),
  /** Owner only: promote "ordering" to live although hard gates are not green. */
  overrideGates: z
    .object({
      reason: z.string().trim().min(10, 'Type why you override (at least 10 characters)').max(300),
    })
    .optional(),
});
export type SetTownFeatureInput = z.infer<typeof SetTownFeatureInputSchema>;

export const TownFeatureDetailSchema = TownFeatureCellSchema.extend({
  townId: z.uuid(),
  townName: z.string(),
  /** Full numbers for launch.manage holders; masked for viewers. */
  pilotPhones: z.array(z.string()),
  pilotRestaurants: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type TownFeatureDetail = z.infer<typeof TownFeatureDetailSchema>;

export const TownFeatureHistoryEntrySchema = z.object({
  id: z.string(),
  townId: z.uuid(),
  townName: z.string(),
  key: TownFeatureKeySchema,
  fromState: TownFeatureStateSchema.nullable(),
  toState: TownFeatureStateSchema,
  actor: z.string(),
  actorName: z.string().nullable(),
  reason: z.string(),
  gateOverride: z.string().nullable(),
  pilotPhoneCount: z.number().int().min(0),
  pilotRestaurantCount: z.number().int().min(0),
  at: z.string(),
});
export type TownFeatureHistoryEntry = z.infer<typeof TownFeatureHistoryEntrySchema>;

export const TownFeatureHistoryQuerySchema = z.object({
  townId: z.uuid().optional(),
  key: TownFeatureKeySchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TownFeatureHistoryQuery = z.infer<typeof TownFeatureHistoryQuerySchema>;

export const TownFeatureParamsSchema = z.object({
  townId: z.uuid(),
  key: TownFeatureKeySchema,
});

/* ---------------- Pilot gates ---------------- */

export const LAUNCH_GATE_KEYS = [
  'paying_restaurants',
  'orders_per_day',
  'no_rider_cancels',
  'on_time',
  'refund_rate',
  'rider_supply',
  'support_sla',
  'crash_free',
  'legal_reviewed',
  'backup_drill',
  'load_test',
] as const;
export const LaunchGateKeySchema = z.enum(LAUNCH_GATE_KEYS);
export type LaunchGateKey = z.infer<typeof LaunchGateKeySchema>;

export const GATE_STATUSES = ['green', 'amber', 'red'] as const;
export const GateStatusSchema = z.enum(GATE_STATUSES);
export type GateStatus = z.infer<typeof GateStatusSchema>;

export interface LaunchGateDef {
  key: LaunchGateKey;
  label: string;
  description: string;
  /** Hard gates must all be green before "ordering" goes live (owner may override). */
  hard: boolean;
  /** "min": value must be ≥ threshold; "max": ≤ threshold; "check": done / not done. */
  direction: 'min' | 'max' | 'check';
  unit: string;
  /** Where the number comes from. */
  source: 'auto' | 'manual';
  /** Settings key holding the threshold (town scope), when the gate has one. */
  configKey: string | null;
}

export const LAUNCH_GATE_DEFS: readonly LaunchGateDef[] = [
  {
    key: 'paying_restaurants',
    label: 'Paying restaurants',
    description: 'Outlets on an active paid plan (W6), not in the free launch period.',
    hard: true,
    direction: 'min',
    unit: 'restaurants',
    source: 'auto',
    configKey: 'launch.gate_paying_restaurants_min',
  },
  {
    key: 'orders_per_day',
    label: 'Orders per day',
    description: 'Delivered app orders per day, 7-day rolling average.',
    hard: true,
    direction: 'min',
    unit: 'orders/day',
    source: 'auto',
    configKey: 'launch.gate_orders_per_day_min',
  },
  {
    key: 'no_rider_cancels',
    label: 'No-rider cancels',
    description: 'Orders cancelled because no rider was found, last 7 days.',
    hard: true,
    direction: 'max',
    unit: '%',
    source: 'auto',
    configKey: 'launch.gate_no_rider_cancel_max_percent',
  },
  {
    key: 'on_time',
    label: 'On time',
    description: 'Delivered by the promised time (W14), last 7 days.',
    hard: false,
    direction: 'min',
    unit: '%',
    source: 'auto',
    configKey: 'launch.gate_on_time_min_percent',
  },
  {
    key: 'refund_rate',
    label: 'Refund rate',
    description: 'Orders with a refund (W11), last 7 days.',
    hard: false,
    direction: 'max',
    unit: '%',
    source: 'auto',
    configKey: 'launch.gate_refund_max_percent',
  },
  {
    key: 'rider_supply',
    label: 'Rider supply',
    description: 'Riders who delivered per 100 delivered orders a day, last 7 days.',
    hard: false,
    direction: 'min',
    unit: 'riders / 100 orders',
    source: 'auto',
    configKey: 'launch.gate_rider_supply_min',
  },
  {
    key: 'support_sla',
    label: 'Support SLA met',
    description: 'Help tickets answered within their SLA (W16), last 7 days.',
    hard: false,
    direction: 'min',
    unit: '%',
    source: 'auto',
    configKey: 'launch.gate_support_sla_min_percent',
  },
  {
    key: 'crash_free',
    label: 'Crash-free sessions',
    description: 'Entered by hand until app telemetry exists.',
    hard: false,
    direction: 'min',
    unit: '%',
    source: 'manual',
    configKey: 'launch.gate_crash_free_min_percent',
  },
  {
    key: 'legal_reviewed',
    label: 'Legal documents reviewed',
    description: "Every published legal document of the town's country is marked reviewed (W21).",
    hard: true,
    direction: 'check',
    unit: '',
    source: 'auto',
    configKey: null,
  },
  {
    key: 'backup_drill',
    label: 'Backup restore drill',
    description: 'A restore drill passed (date + evidence link), within the last 90 days.',
    hard: true,
    direction: 'check',
    unit: '',
    source: 'manual',
    configKey: null,
  },
  {
    key: 'load_test',
    label: 'Load test',
    description: 'The LOADTEST.md profile passed (date + evidence link), within the last 90 days.',
    hard: true,
    direction: 'check',
    unit: '',
    source: 'manual',
    configKey: null,
  },
];

export const LAUNCH_GATE_LABELS: Record<LaunchGateKey, string> = Object.fromEntries(
  LAUNCH_GATE_DEFS.map((def) => [def.key, def.label]),
) as Record<LaunchGateKey, string>;

/** Manual checks older than this turn amber (soft) or red (hard). */
export const MANUAL_CHECK_MAX_AGE_DAYS = 90;

export const GateTrendPointSchema = z.object({
  date: z.string(),
  value: z.number().nullable(),
  status: GateStatusSchema,
});
export type GateTrendPoint = z.infer<typeof GateTrendPointSchema>;

export const LaunchGateResultSchema = z.object({
  key: LaunchGateKeySchema,
  label: z.string(),
  hard: z.boolean(),
  status: GateStatusSchema,
  value: z.number().nullable(),
  threshold: z.number().nullable(),
  unit: z.string(),
  /** Plain words: why it is green / amber / red. */
  detail: z.string(),
  /** Numerator / denominator behind a percentage, when there is one. */
  sample: z.object({ count: z.number().int(), of: z.number().int() }).nullable(),
  trend: z.array(GateTrendPointSchema),
});
export type LaunchGateResult = z.infer<typeof LaunchGateResultSchema>;

export const TownGatesSchema = z.object({
  townId: z.uuid(),
  townName: z.string(),
  country: CountryCodeSchema,
  orderingState: TownFeatureStateSchema,
  computedAt: z.string().nullable(),
  /** All hard gates green. */
  go: z.boolean(),
  hardGreen: z.number().int(),
  hardTotal: z.number().int(),
  blocking: z.array(z.string()),
  warnings: z.array(z.string()),
  gates: z.array(LaunchGateResultSchema),
});
export type TownGates = z.infer<typeof TownGatesSchema>;

export const LaunchGatesOverviewSchema = z.object({
  towns: z.array(TownGatesSchema),
  manualChecks: z.array(z.lazy(() => LaunchManualCheckSchema)),
});
export type LaunchGatesOverview = z.infer<typeof LaunchGatesOverviewSchema>;

export const MANUAL_CHECK_KEYS = ['crash_free', 'backup_drill', 'load_test'] as const;
export const ManualCheckKeySchema = z.enum(MANUAL_CHECK_KEYS);
export type ManualCheckKey = z.infer<typeof ManualCheckKeySchema>;

export const LaunchManualCheckSchema = z.object({
  id: z.string(),
  key: ManualCheckKeySchema,
  /** null = applies to every town. */
  townId: z.uuid().nullable(),
  townName: z.string().nullable(),
  passed: z.boolean(),
  value: z.number().nullable(),
  doneOn: z.string(),
  evidenceUrl: z.string().nullable(),
  note: z.string(),
  recordedBy: z.string(),
  recordedByName: z.string().nullable(),
  recordedAt: z.string(),
});
export type LaunchManualCheck = z.infer<typeof LaunchManualCheckSchema>;

export const RecordManualCheckInputSchema = z.object({
  key: ManualCheckKeySchema,
  townId: z.uuid().nullable().optional(),
  passed: z.boolean(),
  value: z.number().min(0).max(100).nullable().optional(),
  doneOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  evidenceUrl: z.url().max(500).nullable().optional(),
  note: z.string().trim().min(3, 'Add a short note').max(500),
});
export type RecordManualCheckInput = z.infer<typeof RecordManualCheckInputSchema>;

export const LaunchGatesQuerySchema = z.object({
  townId: z.uuid().optional(),
  country: CountryCodeSchema.optional(),
});
export type LaunchGatesQuery = z.infer<typeof LaunchGatesQuerySchema>;

/* ---------------- App versions ---------------- */

export const APP_KEYS = ['customer', 'partner', 'rider', 'mi'] as const;
export const AppKeySchema = z.enum(APP_KEYS);
export type AppKey = z.infer<typeof AppKeySchema>;

export const APP_LABELS: Record<AppKey, string> = {
  customer: 'Capsico (customer)',
  partner: 'Capsico Partner',
  rider: 'Capsico Rider',
  mi: 'Capsico MI',
};

export const APP_PACKAGE_IDS: Record<AppKey, string> = {
  customer: 'com.capsico.customer',
  partner: 'com.capsico.partner',
  rider: 'com.capsico.rider',
  mi: 'com.capsico.mi',
};

export const APP_PLATFORMS = ['android', 'ios', 'web'] as const;
export const AppPlatformSchema = z.enum(APP_PLATFORMS);
export type AppPlatform = z.infer<typeof AppPlatformSchema>;

export const APP_VERDICTS = ['ok', 'update_available', 'update_required'] as const;
export type AppVerdict = (typeof APP_VERDICTS)[number];

const VersionNameSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}\.\d{1,3}\.\d{1,4}$/, 'Use major.minor.patch (for example 1.4.2)');

export const AppVersionCheckQuerySchema = z.object({
  app: AppKeySchema,
  platform: AppPlatformSchema,
  /** The running build (Android versionCode). Without it the verdict is "ok". */
  build: z.coerce.number().int().min(0).max(2_000_000_000).optional(),
});
export type AppVersionCheckQuery = z.infer<typeof AppVersionCheckQuerySchema>;

export const AppVersionCheckSchema = z.object({
  app: AppKeySchema,
  platform: AppPlatformSchema,
  latestVersion: z.string().nullable(),
  latestBuild: z.number().int().nullable(),
  minSupportedBuild: z.number().int().nullable(),
  verdict: z.enum(APP_VERDICTS),
  notes: z.string().nullable(),
  storeUrl: z.string().nullable(),
});
export type AppVersionCheck = z.infer<typeof AppVersionCheckSchema>;

export const AppVersionRecordSchema = z.object({
  app: AppKeySchema,
  platform: AppPlatformSchema,
  latestVersion: z.string(),
  latestBuild: z.number().int(),
  minSupportedBuild: z.number().int(),
  forceUpdate: z.boolean(),
  notes: z.string().nullable(),
  storeUrl: z.string().nullable(),
  updatedBy: z.string(),
  updatedByName: z.string().nullable(),
  updatedAt: z.string(),
  version: z.number().int().min(1),
});
export type AppVersionRecord = z.infer<typeof AppVersionRecordSchema>;

export const AppVersionListSchema = z.object({ rows: z.array(AppVersionRecordSchema) });
export type AppVersionList = z.infer<typeof AppVersionListSchema>;

export const UpsertAppVersionInputSchema = z
  .object({
    latestVersion: VersionNameSchema,
    latestBuild: z.number().int().min(1).max(2_000_000_000),
    minSupportedBuild: z.number().int().min(1).max(2_000_000_000),
    forceUpdate: z.boolean(),
    notes: z.string().trim().max(500).nullable().optional(),
    storeUrl: z.url().max(500).nullable().optional(),
    reason: z.string().trim().min(3, 'Say why').max(300),
    /** null when the app + platform has no row yet. */
    expectedVersion: z.number().int().min(1).nullable(),
  })
  .refine((v) => v.minSupportedBuild <= v.latestBuild, {
    message: 'The minimum supported build cannot be newer than the latest build',
    path: ['minSupportedBuild'],
  });
export type UpsertAppVersionInput = z.infer<typeof UpsertAppVersionInputSchema>;

export const AppVersionParamsSchema = z.object({ app: AppKeySchema, platform: AppPlatformSchema });

/* ---------------- Launch blockers ---------------- */

export const BLOCKER_STATUSES = ['open', 'in_progress', 'done', 'wont_fix'] as const;
export const BlockerStatusSchema = z.enum(BLOCKER_STATUSES);
export type BlockerStatus = z.infer<typeof BlockerStatusSchema>;

export const BLOCKER_STATUS_LABELS: Record<BlockerStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
  wont_fix: "Won't fix",
};

export const BLOCKER_AREAS = [
  'legal',
  'payments',
  'messaging',
  'stores',
  'infrastructure',
  'operations',
  'engineering',
] as const;
export const BlockerAreaSchema = z.enum(BLOCKER_AREAS);
export type BlockerArea = z.infer<typeof BlockerAreaSchema>;

export const LaunchBlockerSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  area: BlockerAreaSchema,
  /** Where it came from ("W21 runbook", "pilot gate", "added in Capsico OS"). */
  source: z.string(),
  owner: z.string().nullable(),
  dueOn: z.string().nullable(),
  status: BlockerStatusSchema,
  townId: z.uuid().nullable(),
  townName: z.string().nullable(),
  /** Blocks a town (or every town) from going live while open. */
  blocksLaunch: z.boolean(),
  note: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string(),
  updatedAt: z.string(),
  version: z.number().int().min(1),
});
export type LaunchBlocker = z.infer<typeof LaunchBlockerSchema>;

export const LaunchBlockerListSchema = z.object({
  rows: z.array(LaunchBlockerSchema),
  counts: z.record(BlockerStatusSchema, z.number().int()),
});
export type LaunchBlockerList = z.infer<typeof LaunchBlockerListSchema>;

export const LaunchBlockerQuerySchema = z.object({
  status: BlockerStatusSchema.optional(),
  townId: z.uuid().optional(),
  openOnly: z.coerce.boolean().optional(),
});
export type LaunchBlockerQuery = z.infer<typeof LaunchBlockerQuerySchema>;

export const CreateLaunchBlockerInputSchema = z.object({
  title: z.string().trim().min(5).max(200),
  area: BlockerAreaSchema,
  owner: z.string().trim().max(80).nullable().optional(),
  dueOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  townId: z.uuid().nullable().optional(),
  blocksLaunch: z.boolean().default(true),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type CreateLaunchBlockerInput = z.infer<typeof CreateLaunchBlockerInputSchema>;

export const UpdateLaunchBlockerInputSchema = z.object({
  title: z.string().trim().min(5).max(200).optional(),
  area: BlockerAreaSchema.optional(),
  owner: z.string().trim().max(80).nullable().optional(),
  dueOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  status: BlockerStatusSchema.optional(),
  blocksLaunch: z.boolean().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  reason: z.string().trim().min(3, 'Say why').max(300),
  expectedVersion: z.number().int().min(1),
});
export type UpdateLaunchBlockerInput = z.infer<typeof UpdateLaunchBlockerInputSchema>;

/* ---------------- Launch-day checklist ---------------- */

export const CHECKLIST_PHASES = ['pre', 'during', 'post'] as const;
export const ChecklistPhaseSchema = z.enum(CHECKLIST_PHASES);
export type ChecklistPhase = z.infer<typeof ChecklistPhaseSchema>;

export const CHECKLIST_PHASE_LABELS: Record<ChecklistPhase, string> = {
  pre: 'Before launch',
  during: 'Launch day',
  post: 'After launch',
};

export const LaunchChecklistItemSchema = z.object({
  id: z.uuid(),
  phase: ChecklistPhaseSchema,
  sortOrder: z.number().int(),
  title: z.string(),
  detail: z.string().nullable(),
  /** Per-town tick (null town = the global template row ticked for every town). */
  townId: z.uuid().nullable(),
  done: z.boolean(),
  doneBy: z.string().nullable(),
  doneByName: z.string().nullable(),
  doneAt: z.string().nullable(),
  note: z.string().nullable(),
  version: z.number().int().min(1),
  /** A tick row exists for this item (false: never ticked; send expectedVersion null or 1). */
  hasTick: z.boolean(),
});
export type LaunchChecklistItem = z.infer<typeof LaunchChecklistItemSchema>;

export const LaunchChecklistSchema = z.object({
  townId: z.uuid().nullable(),
  items: z.array(LaunchChecklistItemSchema),
  done: z.number().int(),
  total: z.number().int(),
});
export type LaunchChecklist = z.infer<typeof LaunchChecklistSchema>;

export const LaunchChecklistQuerySchema = z.object({ townId: z.uuid().optional() });
export type LaunchChecklistQuery = z.infer<typeof LaunchChecklistQuerySchema>;

export const TickChecklistInputSchema = z.object({
  townId: z.uuid().nullable().optional(),
  done: z.boolean(),
  note: z.string().trim().max(300).nullable().optional(),
  /** null when this town has no tick row for the item yet. */
  expectedVersion: z.number().int().min(1).nullable(),
});
export type TickChecklistInput = z.infer<typeof TickChecklistInputSchema>;

/* ---------------- Launch home ---------------- */

export const LaunchHomeTownSchema = z.object({
  townId: z.uuid(),
  townName: z.string(),
  country: CountryCodeSchema,
  townStatus: z.enum(['planned', 'live', 'paused']),
  orderingState: TownFeatureStateSchema,
  liveFeatures: z.number().int(),
  pilotFeatures: z.number().int(),
  go: z.boolean().nullable(),
  hardGreen: z.number().int(),
  hardTotal: z.number().int(),
  gatesComputedAt: z.string().nullable(),
  openBlockers: z.number().int(),
  checklistDone: z.number().int(),
  checklistTotal: z.number().int(),
});
export type LaunchHomeTown = z.infer<typeof LaunchHomeTownSchema>;

export const LaunchHomeSchema = z.object({
  launchOn: z.boolean(),
  defaultState: z.enum(['live', 'off']),
  towns: z.array(LaunchHomeTownSchema),
  openBlockers: z.array(LaunchBlockerSchema),
  appVersions: z.array(AppVersionRecordSchema),
});
export type LaunchHome = z.infer<typeof LaunchHomeSchema>;

/* ---------------- Legal: mark as reviewed (owner only) ---------------- */

export const MarkLegalReviewedInputSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(10, 'Say who reviewed it and how (at least 10 characters)')
    .max(500),
  expectedVersion: z.number().int().min(1),
});
export type MarkLegalReviewedInput = z.infer<typeof MarkLegalReviewedInputSchema>;

/** Countries covered by launch views (helper for the web filters). */
export const LAUNCH_COUNTRIES: readonly CountryCode[] = ['IN', 'NP'];
